package com.banyan.flightviewer.data

import com.banyan.flightviewer.state.AtcStreamInfo

/**
 * Static lookup of LiveATC feeds for supported airports.
 *
 * Keyed by ICAO code. IATA codes are resolved to ICAO via the hardcoded aliases below.
 * Each airport lists the audio streams that are known to exist on LiveATC; when a
 * feed doesn't actually resolve at runtime, MediaPlayer surfaces the error through
 * AtcAudioManager's error handler.
 *
 * Feed naming follows LiveATC's conventions:
 *   - `{icao}_twr` -- Tower
 *   - `{icao}_gnd` -- Ground
 *   - `{icao}_app` / `{icao}_app_dep` -- Approach/Departure (varies per airport)
 *
 * Legal note: feed URLs are bundled for POC only. Before ship, either obtain LiveATC
 * permission for redistribution, or replace this with a user-supplied URL flow.
 */
object AtcFeedDatabase {

    /**
     * IATA -> ICAO aliases for airports in the feed map. The map can be queried with
     * either form. Only airports that actually have feeds need entries here.
     */
    private val iataToIcao: Map<String, String> = mapOf(
        // Northeast
        "JFK" to "KJFK", "LGA" to "KLGA", "EWR" to "KEWR", "BOS" to "KBOS",
        "IAD" to "KIAD", "DCA" to "KDCA", "PHL" to "KPHL",
        // Southeast (non-FL)
        "ATL" to "KATL", "CLT" to "KCLT",
        // Florida
        "MIA" to "KMIA", "MCO" to "KMCO", "TPA" to "KTPA", "FLL" to "KFLL",
        "SRQ" to "KSRQ", "JAX" to "KJAX", "PBI" to "KPBI", "RSW" to "KRSW",
        "DAB" to "KDAB", "SFB" to "KSFB", "PIE" to "KPIE",
        "TLH" to "KTLH", "PNS" to "KPNS", "GNV" to "KGNV", "MLB" to "KMLB",
        "LAL" to "KLAL", "FXE" to "KFXE", "BCT" to "KBCT",
        // Midwest
        "ORD" to "KORD", "MDW" to "KMDW", "DTW" to "KDTW",
        "MSP" to "KMSP", "STL" to "KSTL",
        // South/Southwest
        "DFW" to "KDFW", "IAH" to "KIAH", "DEN" to "KDEN",
        "LAS" to "KLAS", "PHX" to "KPHX", "AUS" to "KAUS",
        // West
        "LAX" to "KLAX", "SFO" to "KSFO", "SEA" to "KSEA",
        "SAN" to "KSAN", "PDX" to "KPDX", "SLC" to "KSLC",
    )

    /**
     * Build a standard Tower/Ground feed pair for the given ICAO code.
     * Most major US airports follow the `{icao_lower}_twr` / `{icao_lower}_gnd` pattern.
     */
    private fun twrGnd(icao: String): List<AtcStreamInfo> {
        val lower = icao.lowercase()
        return listOf(
            AtcStreamInfo(airportCode = icao, feedName = "Tower", url = "https://d.liveatc.net/${lower}_twr"),
            AtcStreamInfo(airportCode = icao, feedName = "Ground", url = "https://d.liveatc.net/${lower}_gnd"),
        )
    }

    /**
     * Build a Tower-only feed for smaller airports that don't have a separate Ground feed
     * on LiveATC (often because Tower and Ground share a frequency at Class D fields).
     */
    private fun twrOnly(icao: String): List<AtcStreamInfo> {
        val lower = icao.lowercase()
        return listOf(
            AtcStreamInfo(airportCode = icao, feedName = "Tower", url = "https://d.liveatc.net/${lower}_twr"),
        )
    }

    /**
     * Build a single feed with an arbitrary LiveATC mount name. Use this for airports
     * whose mount points don't follow the `{icao}_twr` / `{icao}_gnd` convention
     * (e.g. numbered mounts like `ktpa2`, combined feeds like `kjax_gnd_twr`).
     */
    private fun feed(icao: String, name: String, mount: String): AtcStreamInfo =
        AtcStreamInfo(airportCode = icao, feedName = name, url = "https://d.liveatc.net/$mount")

    /**
     * Build a special-event feed. Feeds sharing the same non-blank [category]
     * render as a titled group at the top of the airport panel, above the
     * regular ATC feeds. Use for fly-ins, airshows, and similar one-off
     * broadcasts that aren't part of day-to-day airport operations.
     */
    private fun eventFeed(icao: String, name: String, mount: String, category: String): AtcStreamInfo =
        AtcStreamInfo(
            airportCode = icao,
            feedName = name,
            url = "https://d.liveatc.net/$mount",
            category = category,
        )

    /** Add an Approach/Departure feed to an existing list. */
    private fun withAppDep(base: List<AtcStreamInfo>, icao: String, suffix: String = "app"): List<AtcStreamInfo> {
        val lower = icao.lowercase()
        return base + AtcStreamInfo(
            airportCode = icao,
            feedName = "Approach/Departure",
            url = "https://d.liveatc.net/${lower}_$suffix",
        )
    }

    private val feeds: Map<String, List<AtcStreamInfo>> = mapOf(
        // --- New York metro ---
        "KJFK" to withAppDep(twrGnd("KJFK"), "KJFK", "app_cd"),
        "KLGA" to withAppDep(twrGnd("KLGA"), "KLGA", "app_cd"),
        "KEWR" to withAppDep(twrGnd("KEWR"), "KEWR", "app_cd"),

        // --- Northeast ---
        "KBOS" to withAppDep(twrGnd("KBOS"), "KBOS"),
        "KIAD" to withAppDep(twrGnd("KIAD"), "KIAD"),
        "KDCA" to withAppDep(twrGnd("KDCA"), "KDCA"),
        "KPHL" to withAppDep(twrGnd("KPHL"), "KPHL"),

        // --- Southeast (non-Florida) ---
        "KATL" to withAppDep(twrGnd("KATL"), "KATL"),
        "KCLT" to withAppDep(twrGnd("KCLT"), "KCLT"),

        // --- Florida ---
        // Mounts verified against LiveATC listings. Many FL airports use numeric or
        // combined-facility mount names instead of the standard {icao}_twr convention.
        "KMIA" to listOf(
            feed("KMIA", "Tower", "kmia_twr"),
            feed("KMIA", "Tower (North)", "kmia3_twr_1183"),
            feed("KMIA", "Tower (South)", "kmia3_twr_1239"),
            feed("KMIA", "Clearance Delivery", "kmia3_del"),
            feed("KMIA", "Approach", "kmia_app"),
            feed("KMIA", "Approach #2", "kmia_app2"),
            feed("KMIA", "Departure", "kmia_dep"),
        ),
        "KMCO" to listOf(
            feed("KMCO", "Tower/App", "kmco"),
            feed("KMCO", "Tower (East)", "kmco1"),
            feed("KMCO", "Approach (Final)", "kmco6"),
            feed("KMCO", "App/Dep (124.8)", "korl_kmco_app_124800"),
        ),
        "KTPA" to listOf(
            feed("KTPA", "Gnd/Twr/App", "ktpa2"),
            feed("KTPA", "App/Dep (135.5)", "ktpa1_app_135500"),
        ),
        "KFLL" to listOf(
            feed("KFLL", "Tower", "kfll1_twr"),
            feed("KFLL", "Tower (Main)", "kfll1_twr1"),
            feed("KFLL", "Ground", "kfll1_gnd"),
            feed("KFLL", "Ground (Main)", "kfll1_gnd1"),
        ),
        "KJAX" to listOf(
            feed("KJAX", "Gnd/Twr", "kjax_gnd_twr"),
            feed("KJAX", "App/Dep", "kjax_app_dep"),
        ),
        "KPBI" to listOf(
            feed("KPBI", "Tower", "kpbi2_twr"),
            feed("KPBI", "Gnd/Twr", "kpbi2_gnd_twr"),
        ),
        "KRSW" to listOf(
            feed("KRSW", "Gnd/Twr/App/Dep", "krsw2"),
        ),
        "KSRQ" to listOf(
            // Verified via https://www.liveatc.net/search/?icao=srq -- KSRQ has
            // three local feeds of its own. (The ksrq*_zma mounts carry Miami
            // Center traffic fed from Sarasota; not useful for airport-local
            // surface/approach ops.)
            feed("KSRQ", "Tower", "ksrq1_twr"),
            feed("KSRQ", "Del/Gnd", "ksrq1_del_gnd"),
            feed("KSRQ", "App/Dep", "ksrq1_app_dep"),
        ),
        "KDAB" to listOf(
            feed("KDAB", "Tower", "kdab_twr_pri"),
            feed("KDAB", "Clearance/Ground", "kdab_del_gnd"),
            feed("KDAB", "App/Dep", "kdab_app_all"),
        ),
        "KSFB" to listOf(
            feed("KSFB", "Tower", "ksfb"),
            feed("KSFB", "Tower (Main)", "ksfb3_twr1"),
        ),
        "KPIE" to listOf(
            feed("KPIE", "Del/Gnd/Twr", "kpie"),
        ),
        "KTLH" to listOf(
            feed("KTLH", "Del/Gnd/Twr/App", "ktlh"),
        ),
        "KPNS" to listOf(
            feed("KPNS", "Tower", "kpns1_twr"),
            feed("KPNS", "Ground", "kpns1_gnd"),
        ),
        "KGNV" to listOf(
            feed("KGNV", "Gnd/Twr", "kgnv"),
        ),
        "KMLB" to listOf(
            feed("KMLB", "Twr/App", "kmlb"),
            feed("KMLB", "Ground", "kmlb1_gnd"),
        ),
        "KLAL" to listOf(
            // Full feed list verified against https://www.liveatc.net/search/?icao=klal .
            // Lakeland hosts Sun 'n Fun each spring, so the feed set is unusually
            // large. The event-specific mounts (Sun 'n Fun Radio, Air Show,
            // Military Performers, Paradise City) are categorised so the UI
            // renders them as a distinct section above the regular airport
            // feeds -- they're silent off-season and shouldn't be mistaken
            // for day-to-day operations.
            //
            // The legacy `klal_twr` mount that used to be here is no longer
            // listed on LiveATC; its replacements are the North/South splits
            // below. Warbird Advisory / Lake Parker Arrival are Sun 'n Fun
            // adjacent but they ARE ATC ops (not programming), so they stay
            // in the regular group.
            eventFeed("KLAL", "Sun 'n Fun Radio", "sunfunlive1", category = "Sun 'n Fun"),
            eventFeed("KLAL", "Sun 'n Fun Air Show", "klal_air", category = "Sun 'n Fun"),
            eventFeed("KLAL", "Sun 'n Fun Military Performers", "klal_mil", category = "Sun 'n Fun"),
            eventFeed("KLAL", "Sun 'n Fun Paradise City", "snfpc", category = "Sun 'n Fun"),
            feed("KLAL", "Tower (North) #1", "klal_twr_north"),
            feed("KLAL", "Tower (North) #2", "klal1_twr_north"),
            feed("KLAL", "Tower (South)", "klal1_twr_south"),
            feed("KLAL", "Ground", "klal1_gnd"),
            feed("KLAL", "Ground/Advisory", "klal_gnd"),
            feed("KLAL", "ATIS", "klal_atis"),
            feed("KLAL", "Tampa App/Dep #1", "klal_app_dep"),
            feed("KLAL", "Tampa App/Dep #2", "klal1_app"),
            feed("KLAL", "Helicopter", "klal1_heli"),
            feed("KLAL", "Warbird Advisory", "klal1_wb"),
            feed("KLAL", "Lake Parker Arrival", "klal8_twr"),
        ),
        "KFXE" to listOf(
            feed("KFXE", "Tower #1", "kfxe3"),
            feed("KFXE", "Tower #2", "kfxe3_twr2"),
            feed("KFXE", "Ground", "kfxe3_gnd"),
            feed("KFXE", "Practice Area", "kfxe_pa"),
        ),
        "KBCT" to listOf(
            feed("KBCT", "Gnd/Tower", "kbct1_gnd_twr"),
        ),
        // Not bundled -- no confirmed LiveATC feeds:
        //   KECP, KAPF, KOPF, KORL, KISM

        // --- Midwest ---
        "KORD" to withAppDep(twrGnd("KORD"), "KORD"),
        "KMDW" to twrGnd("KMDW"),
        "KDTW" to withAppDep(twrGnd("KDTW"), "KDTW"),
        "KMSP" to withAppDep(twrGnd("KMSP"), "KMSP"),
        "KSTL" to twrGnd("KSTL"),

        // --- South/Southwest ---
        "KDFW" to withAppDep(twrGnd("KDFW"), "KDFW"),
        "KIAH" to withAppDep(twrGnd("KIAH"), "KIAH"),
        "KDEN" to withAppDep(twrGnd("KDEN"), "KDEN"),
        "KLAS" to withAppDep(twrGnd("KLAS"), "KLAS"),
        "KPHX" to withAppDep(twrGnd("KPHX"), "KPHX"),
        "KAUS" to twrGnd("KAUS"),

        // --- West ---
        "KLAX" to withAppDep(twrGnd("KLAX"), "KLAX", "app_dep"),
        "KSFO" to withAppDep(twrGnd("KSFO"), "KSFO"),
        "KSEA" to withAppDep(twrGnd("KSEA"), "KSEA"),
        "KSAN" to twrGnd("KSAN"),
        "KPDX" to twrGnd("KPDX"),
        "KSLC" to twrGnd("KSLC"),
    )

    /** Resolve IATA or ICAO input to the canonical ICAO key. */
    private fun resolveIcao(code: String): String {
        val up = code.uppercase()
        return iataToIcao[up] ?: up
    }

    /** Returns feeds for the given airport, or an empty list if none are bundled. */
    fun feedsFor(airportCode: String): List<AtcStreamInfo> {
        return feeds[resolveIcao(airportCode)] ?: emptyList()
    }

    /** True if any ATC feeds are bundled for this airport. */
    fun hasFeeds(airportCode: String): Boolean = feedsFor(airportCode).isNotEmpty()

    /**
     * Set of all LiveATC mount names referenced by bundled feeds. Used by the
     * server's audio proxy as a whitelist to prevent open-proxy abuse: only
     * mounts that appear here are allowed through `/api/atc/stream`.
     */
    fun allMounts(): Set<String> {
        val mounts = mutableSetOf<String>()
        for (list in feeds.values) {
            for (stream in list) {
                // url is "https://d.liveatc.net/{mount}" -- take everything after the last "/"
                val mount = stream.url.substringAfterLast('/')
                if (mount.isNotBlank()) mounts.add(mount)
            }
        }
        return mounts
    }
}
